import { RequirementFriendlyDescription } from "../requirementFriendlyDescription.js";
import { a3Part1DataSchema, a3Part2DataSchema, } from "../schemas/mhaForms/admission/a3.js";
import { a3UploadablePart1DataSchema, a3UploadablePart2DataSchema, } from "../schemas/mhaForms/admission/uploadable/a3.js";
import { isNonNull } from "../util/predicates.js";
import { validateSection2ExaminationDate } from "./section2.js";
const a3Part1Signing = {
    userOrTeamRequirement: { accreditation: ["doctor"], teamType: [] },
};
export const a3Part1Template = {
    signing: a3Part1Signing,
    formBuilderFields: [
        {
            type: "readonly",
            label: "Name and address of patient",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode].join(", "),
            ],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "label",
            showStaticLabel: true,
            label: [
                "We, registered medical practitioners, recommend that this patient be admitted to a hospital for assessment in accordance with Part 2 of the Mental Health Act 1983. ",
            ],
        },
        {
            type: "practitioner",
            label: "My email address, name, and address",
            field: "user",
        },
        {
            type: "date",
            label: "Enter the date you last examined this patient on",
            field: "lastExaminedDate",
            maximum: (today) => today,
            warningFn: validateSection2ExaminationDate,
        },
        {
            type: "branch",
            field: "previousAcquaintance",
            label: "Did you have previous acquaintance with the patient before you conducted the examination?",
            branches: [
                { fieldValue: true, label: "Yes", fieldsOwned: [], fields: [] },
                { fieldValue: false, label: "No", fieldsOwned: [], fields: [] },
            ],
        },
        {
            type: "section-12-confirmation-checkbox",
            checkboxLabel: "Are you approved under section 12 of the Act as having special experience in the diagnosis or treatment of mental disorder?",
        },
        {
            type: "label",
            showStaticLabel: true,
            label: [
                "(a) In our opinion, " +
                    "this patient is suffering from mental disorder " +
                    "of a nature or degree " +
                    "which warrants the detention of the patient in hospital " +
                    "for assessment (or for assessment followed by medical treatment) " +
                    "for at least a limited period.",
            ],
        },
        {
            type: "multi-select",
            label: "(b) In our opinion, this patient ought to be so detained",
            options: [
                {
                    field: "necessaryForPatientsOwnHealth",
                    label: "in the interests of the patient's own health",
                },
                {
                    field: "necessaryForPatientsOwnSafety",
                    label: "in the interests of the patient's own safety",
                },
                {
                    field: "necessaryForProtectionOfOtherPersons",
                    label: "with a view to the protection of other persons.",
                },
            ],
        },
        {
            type: "textbox",
            field: "reasons",
            label: "Our reasons for these opinions are: " +
                "(Your reasons should cover both (a) and (b) above. " +
                "As part of them: describe the patient's symptoms and behaviour and explain how those symptoms and behaviour " +
                "lead you to your opinion; explain why the patient ought to be admitted to hospital and why informal admission " +
                "is not appropriate).",
            rows: "10",
            enableAvtDictation: true,
        },
    ],
    dataSchema: a3Part1DataSchema,
};
const a3Part2Signing = {
    userOrTeamRequirement: {
        accreditation: ["doctor"],
        teamType: [],
        friendlyDescription: RequirementFriendlyDescription.registeredMedicalPractitioner,
    },
};
export const a3Part2Template = {
    signing: a3Part2Signing,
    formBuilderFields: [
        {
            type: "practitioner",
            label: "My email address, name, and address",
            field: "user",
        },
        {
            type: "date",
            label: "Enter the date you last examined this patient on",
            field: "lastExaminedDate",
            maximum: (today) => today,
            warningFn: validateSection2ExaminationDate,
        },
        {
            type: "branch",
            field: "previousAcquaintance",
            label: "Did you have previous acquaintance with the patient before you conducted the examination?",
            branches: [
                { fieldValue: true, label: "Yes", fieldsOwned: [], fields: [] },
                { fieldValue: false, label: "No", fieldsOwned: [], fields: [] },
            ],
        },
        {
            type: "section-12-confirmation-checkbox",
            checkboxLabel: "I am approved under section 12 of the Act as having special experience in the diagnosis or treatment of mental disorder",
        },
    ],
    dataSchema: a3Part2DataSchema,
};
export const a3UploadablePart1Template = {
    signing: a3Part1Signing,
    formBuilderFields: [
        {
            type: "readonly",
            label: "Name and address of patient",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode].join(", "),
            ],
        },
        {
            type: "name-address",
            field: "nameAndAddressOfSignee",
            nameLabel: "Name of first practitioner",
            addressLabel: "Address of first practitioner",
        },
        {
            type: "textbox",
            label: "Email address of first practitioner",
            field: "emailAddressOfSignee",
        },
        {
            type: "date",
            label: "Date first practitioner last examined this patient on",
            field: "lastExaminedDate",
            maximum: (today) => today,
            warningFn: validateSection2ExaminationDate,
        },
        {
            type: "branch",
            field: "previousAcquaintance",
            label: "Did first practitioner have previous acquaintance with the patient?",
            branches: [
                { fieldValue: true, label: "Yes", fieldsOwned: [], fields: [] },
                { fieldValue: false, label: "No", fieldsOwned: [], fields: [] },
            ],
        },
        {
            type: "branch",
            field: "practitionerApprovedUnderSection12",
            label: "Is first practitioner approved under section 12 of the Act?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        {
            type: "multi-select",
            label: "Confirm detention criteria",
            options: [
                {
                    field: "necessaryForPatientsOwnHealth",
                    label: "in the interests of the patient's own health",
                },
                {
                    field: "necessaryForPatientsOwnSafety",
                    label: "in the interests of the patient's own safety",
                },
                {
                    field: "necessaryForProtectionOfOtherPersons",
                    label: "with a view to the protection of other persons",
                },
            ],
        },
        {
            type: "date",
            field: "signatureDate",
            label: "First practitioners date of signature",
            maximum: (today) => today,
        },
    ],
    dataSchema: a3UploadablePart1DataSchema,
};
export const a3UploadablePart2Template = {
    signing: a3Part2Signing,
    formBuilderFields: [
        {
            type: "name-address",
            field: "nameAndAddressOfSignee",
            nameLabel: "Name of second practitioner",
            addressLabel: "Address of second practitioner",
        },
        {
            type: "textbox",
            label: "Email address of second practitioner",
            field: "emailAddressOfSignee",
        },
        {
            type: "date",
            label: "Date second practitioner last examined this patient on",
            field: "lastExaminedDate",
            maximum: (today) => today,
            warningFn: validateSection2ExaminationDate,
        },
        {
            type: "branch",
            field: "previousAcquaintance",
            label: "Did second practitioner have previous acquaintance with the patient?",
            branches: [
                { fieldValue: true, label: "Yes", fieldsOwned: [], fields: [] },
                { fieldValue: false, label: "No", fieldsOwned: [], fields: [] },
            ],
        },
        {
            type: "branch",
            field: "practitionerApprovedUnderSection12",
            label: "Is second practitioner approved under section 12 of the Act?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        {
            type: "date",
            field: "signatureDate",
            label: "Second practitioners date of signature",
            maximum: (today) => today,
        },
    ],
    dataSchema: a3UploadablePart2DataSchema,
};
export const a3V1Base = {
    id: "mha-a3",
    category: "admission",
    version: "1.0",
    status: "active",
    parts: [a3Part1Template, a3Part2Template],
    formName: "A3",
    section: "2",
    description: "Joint Medical Recommendation for Admission for Assessment",
    extractGenericData: (data) => {
        const part1Data = data[0].data;
        const signedParts = [data[0], data[1]].filter(isNonNull);
        return {
            admissions: {
                section12ApprovedStatus: signedParts.map((part) => part.data?.user?.isSection12Approved || false),
                lastExaminedDate: signedParts.map((part) => part.data?.lastExaminedDate),
                examinerPreviousAcquaintance: part1Data?.previousAcquaintance,
                ...("necessaryForPatientsOwnHealth" in part1Data
                    ? {
                        necessaryForPatientsOwnHealth: part1Data
                            ?.necessaryForPatientsOwnHealth,
                        necessaryForPatientsOwnSafety: part1Data
                            ?.necessaryForPatientsOwnSafety,
                        necessaryForProtectionOfOtherPersons: part1Data?.necessaryForProtectionOfOtherPersons,
                    }
                    : {}),
            },
        };
    },
    uploadable: true,
    uploadablePartTemplates: [
        a3UploadablePart1Template,
        a3UploadablePart2Template,
    ],
};
//# sourceMappingURL=a3.js.map